import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type {
  NumerologyAnalyzeResponse,
  NumerologyRenderResponse,
} from "../../shared/api";
import {
  getNameEquivalents,
  normalizePackage,
  type NumerologyInput,
} from "../../shared/numerology";

function escapeHtml(value: string) {
  return value
    .split("&")
    .join("&amp;")
    .split("<")
    .join("&lt;")
    .split(">")
    .join("&gt;")
    .split('"')
    .join("&quot;")
    .split("'")
    .join("&#039;");
}

function buildNameCells(chars: Array<{ char: string; num: number }>) {
  const letters = chars.map((c) => `<td>${escapeHtml(c.char)}</td>`).join("");
  const numbers = chars.map((c) => `<td>${c.num}</td>`).join("");
  return { letters, numbers };
}

function parseOptionalInt(value: string | null): number | undefined {
  if (value === null) return undefined;
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function buildInputFromSearch(params: URLSearchParams): NumerologyInput | null {
  const firstName = (params.get("firstName") ?? params.get("ad") ?? "").trim();
  const lastName = (params.get("lastName") ?? params.get("soyad") ?? "").trim();

  if (!firstName || !lastName) return null;

  const day = parseOptionalInt(params.get("day") ?? params.get("gun"));
  const month = parseOptionalInt(params.get("month") ?? params.get("ay"));
  const year = parseOptionalInt(params.get("year") ?? params.get("yil"));

  const hasSomeBirth =
    day !== undefined || month !== undefined || year !== undefined;
  const hasFullBirth =
    day !== undefined && month !== undefined && year !== undefined;

  const pkg = normalizePackage(params.get("package") ?? params.get("paket"));

  const base: NumerologyInput = {
    package: pkg,
    firstName,
    lastName,
  };

  if (!hasSomeBirth) return base;
  if (!hasFullBirth) return base;

  return { ...base, day, month, year };
}

export default function PdfOnizleme() {
  const [searchParams] = useSearchParams();
  const queryKey = searchParams.toString();

  const input = useMemo(
    () => buildInputFromSearch(new URLSearchParams(queryKey)),
    [queryKey],
  );

  const [analysis, setAnalysis] = useState<NumerologyAnalyzeResponse | null>(null);
  const [reportHtml, setReportHtml] = useState<string>("");
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    "idle",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!input) {
      setStatus("idle");
      setErrorMessage(
        "Önizleme için URL'e isim parametreleri ekleyin: ?firstName=...&lastName=... (opsiyonel: day, month, year, package)",
      );
      setAnalysis(null);
      setReportHtml("");
      return () => {
        cancelled = true;
      };
    }

    setErrorMessage(null);
    setStatus("loading");
    setAnalysis(null);
    setReportHtml("");

    const run = async () => {
      const analyzeRes = await fetch("/api/numerology/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });

      if (!analyzeRes.ok) {
        const text = await analyzeRes.text().catch(() => "");
        throw new Error(text || "Analiz isteği başarısız");
      }

      const analyzeJson = (await analyzeRes.json()) as NumerologyAnalyzeResponse;
      if (cancelled) return;
      setAnalysis(analyzeJson);

      const reportTemplateParts: string[] = [
        "<p>Merhaba {{ad}},</p>",
        analyzeJson.calculations.hasBirthDate
          ? "<p>Doğum tarihin: {{dogum_tarihi}}. Kader sayın: {{kader_sayisi}} ({{kader_sayisi_sade}}).</p>"
          : "<p>Doğum tarihi verilmediği için isim bazlı analiz oluşturuldu.</p>",
        "<p>İfade numaran: {{ifade_numarasi}}. Kalp arzun: {{kalp_arzusu}}. Kişilik numaran: {{kisilik_numarasi}}.</p>",
      ];

      if (analyzeJson.calculations.hasBirthDate) {
        reportTemplateParts.push(
          "<p>Rengin: {{renk}}. Elementin: {{element}}. Pin kodların: {{pin_kodlari}}.</p>",
        );
        reportTemplateParts.push(
          "<p>Karmik borç sayıları: {{karmik_borc}}. Yılın öngörüsü: {{yilin_ongorusu}}.</p>",
        );
      } else {
        reportTemplateParts.push("<p>Karmik borç sayıları: {{karmik_borc}}.</p>");
      }

      const renderRes = await fetch("/api/numerology/render", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template: reportTemplateParts.join("\n"),
          context: analyzeJson.context,
        }),
      });

      if (!renderRes.ok) {
        const text = await renderRes.text().catch(() => "");
        throw new Error(text || "Rapor render isteği başarısız");
      }

      const renderJson = (await renderRes.json()) as NumerologyRenderResponse;
      if (cancelled) return;
      setReportHtml(renderJson.rendered);
      setStatus("ready");
    };

    run().catch((err: unknown) => {
      if (cancelled) return;
      setStatus("error");
      setErrorMessage(
        err instanceof Error && err.message ? err.message : "Bir hata oluştu",
      );
      setAnalysis(null);
      setReportHtml("");
    });

    return () => {
      cancelled = true;
    };
  }, [input, queryKey]);

  const html = useMemo(() => {
    const fallbackEquivalents =
      input ? getNameEquivalents(input.firstName, input.lastName) : [];

    const resolvedEquivalents = analysis
      ? analysis.nameEquivalents
      : fallbackEquivalents;

    const { letters, numbers } = buildNameCells(
      resolvedEquivalents
        .filter((item) => item.char.trim() !== "")
        .map((item) => ({ char: item.char, num: item.value })),
    );

    const params = new URLSearchParams(queryKey);
    const numerologistName =
      (params.get("numerologistName") ?? params.get("numerologist") ?? "").trim();
    const numerologistPhone = (params.get("numerologistPhone") ?? "").trim();
    const numerologistInstagram =
      (params.get("numerologistInstagram") ?? "").trim();

    const baseFullName =
      analysis?.person.fullName ??
      (input ? `${input.firstName} ${input.lastName}`.trim() : "");

    const firstParts = (analysis?.person.firstName ?? input?.firstName ?? "")
      .split(/\s+/)
      .map((part) => part.trim())
      .filter(Boolean);

    const data: Record<string, string> = {
      client_full_name: baseFullName,
      client_first_name: firstParts[0] ?? "",
      client_middle_name: firstParts.slice(1).join(" "),
      client_last_name: analysis?.person.lastName ?? input?.lastName ?? "",
      fate_number:
        analysis?.calculations.destinyReduced !== undefined
          ? String(analysis.calculations.destinyReduced)
          : "",
      numerologist_name: numerologistName || "",
      numerologist_phone: numerologistPhone || "",
      numerologist_instagram: numerologistInstagram || "",
      name_letters_cells: letters,
      name_numbers_cells: numbers,
      kader_sayisi_text: analysis?.calculations.destinyPath ?? "",
      rengi: analysis?.calculations.color ?? "",
      elementi: analysis?.calculations.element ?? "",
      dogum_gunu_numarasi:
        analysis?.calculations.birthDayNumber !== undefined
          ? String(analysis.calculations.birthDayNumber)
          : "",
      ifade_numarasi:
        analysis?.calculations.expressionNumber !== undefined
          ? String(analysis.calculations.expressionNumber)
          : "",
      kalp_arzusu:
        analysis?.calculations.heartDesireNumber !== undefined
          ? String(analysis.calculations.heartDesireNumber)
          : "",
      kisisilik_numarasi:
        analysis?.calculations.personalityNumber !== undefined
          ? String(analysis.calculations.personalityNumber)
          : "",
      karmik_borc: analysis?.calculations.karmicDebtNumbers.join(", ") ?? "",
      yilin_ongorusu: analysis?.calculations.yearForesight ?? "",
      pin_kodlari: analysis?.calculations.pinCodes
        ? analysis.calculations.pinCodes.join(", ")
        : "",
      cover_background_url:
        "https://cdn.builder.io/api/v1/image/assets%2Fd40888508eef48efa45d21aa5326316d%2F72f577e13aa54ce7baf48a4a242ceb59?format=webp&width=1200",
      content_background_url:
        "https://cdn.builder.io/api/v1/image/assets%2Fd40888508eef48efa45d21aa5326316d%2F3addfe15cd6d4bb9af7410327d9b88b8?format=webp&width=1200",
      report_html:
        reportHtml ||
        (status === "loading"
          ? "<p>Analiz hazırlanıyor...</p>"
          : status === "error"
            ? "<p>Analiz alınamadı.</p>"
            : "<p>Analiz hazır.</p>"),
    };

    const template = String.raw`<!doctype html>
<html lang="tr">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>{{client_full_name}} - Kendi Yolculuğum</title>
  <style>
    @page { size: A4; margin: 0; }
    html, body { height: 100%; }
    body {
      margin: 0;
      font-family: "Times New Roman", Times, serif;
      color: #111;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    body.preview{
      background: #f4f4f6;
      padding: 24px;
    }

    :root{
      --a4w: 210mm;
      --a4h: 297mm;
      --orange: #d97a4a;
      --orange2:#c86a3f;
      --light: #ffffff;
      --muted: rgba(0,0,0,.55);
      --footer-h: 60px;
      --topbar-h: 60px;
      --page-pad-x: 60px;
      --page-pad-y: 70px;
    }

    .page{
      width: 210mm;
      height: 297mm;
      position: relative;
      overflow: hidden;
      background: #fff;
      page-break-after: always;
    }

    body.preview .page{
      margin: 0 auto 24px;
      box-shadow: 0 14px 36px rgba(0,0,0,.14);
      border-radius: 6px;
    }

    .cover{
      background: var(--orange);
      background-image: url("{{cover_background_url}}");
      background-size: 100% 100%;
      background-position: center;
      background-repeat: no-repeat;
    }

    .cover .text-layer{
      position: absolute;
      inset: 0;
      pointer-events: none;
      color: #111;
      font-family: Arial, sans-serif;
    }

    .cover .numerolog-block{
      position: absolute;
      right: 40px;
      top: 420px;
      width: 420px;
      text-align: right;
    }

    .cover .numerolog-block .label{
      font-size: 34px;
      font-weight: 600;
      margin: 0 0 6px 0;
    }

    .cover .numerolog-block .name{
      font-size: 34px;
      font-weight: 700;
      margin: 0;
    }

    .cover .client-last{
      position: absolute;
      left: calc(50% + 10px);
      right: 80px;
      bottom: 120px;
      text-align: left;
      font-size: 48px;
      font-weight: 900;
      letter-spacing: .3px;
      color: #fff;
      text-shadow: 0 1px 0 rgba(0,0,0,.15);
      font-family: Arial, sans-serif;
      line-height: 1.05;
      white-space: nowrap;
    }

    .cover .client-first{
      position: absolute;
      left: 80px;
      right: calc(50% + 10px);
      bottom: 120px;
      text-align: right;
      font-size: 48px;
      font-weight: 900;
      letter-spacing: .3px;
      color: #111;
      font-family: Arial, sans-serif;
      line-height: 1.05;
      white-space: nowrap;
    }


    .cover .fate-number{
      position: absolute;
      right: 120px;
      top: 280px;
      font-family: Arial, sans-serif;
      font-size: 140px;
      font-weight: 800;
      color: rgba(0,0,0,.22);
      line-height: 1;
    }

    .content{
      background: #fff;
      background-image: url("{{content_background_url}}");
      background-size: 100% 100%;
      background-position: center;
      background-repeat: no-repeat;
    }

    .topbar{
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: var(--topbar-h);
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-family: Georgia, "Times New Roman", Times, serif;
      font-size: 10px;
      letter-spacing: .3px;
      padding: 0 var(--page-pad-x);
      box-sizing: border-box;
    }

    .topbar span{ font-weight: 600; white-space: nowrap; }

    .footer{
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: var(--footer-h);
      background: transparent;
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      align-items: center;
      color: #fff;
      font-family: Arial, sans-serif;
      font-size: 10px;
      padding: 0 var(--page-pad-x);
      box-sizing: border-box;
      gap: 12px;
    }

    .footer > div{ white-space: nowrap; overflow: hidden; }
    .footer .left{ text-align: left; justify-self: start; }
    .footer .center{ text-align: center; justify-self: center; }
    .footer .right{ text-align: right; justify-self: end; }

    .content-area{
      position: absolute;
      left: var(--page-pad-x);
      right: var(--page-pad-x);
      top: calc(var(--topbar-h) + 32px);
      bottom: calc(var(--footer-h) + 32px);
    }

    .name-grid{
      width: 100%;
      border: 1px solid rgba(0,0,0,.15);
      border-radius: 4px;
      overflow: hidden;
      margin-bottom: 18px;
      background: rgba(255,255,255,.75);
    }

    .name-grid table{
      width: 100%;
      border-collapse: collapse;
      font-family: Arial, sans-serif;
      font-size: 12px;
      text-transform: lowercase;
    }

    .name-grid td{
      border-right: 1px solid rgba(0,0,0,.12);
      padding: 6px 0;
      text-align: center;
    }

    .name-grid tr:first-child td{ color: rgba(0,0,0,.85); }
    .name-grid tr:last-child td{ color: rgba(0,0,0,.65); }
    .name-grid td:last-child{ border-right: 0; }

    .summary{
      width: 240px;
      border: 1px solid rgba(0,0,0,.18);
      border-radius: 4px;
      overflow: hidden;
      background: rgba(255,255,255,.75);
      font-family: Arial, sans-serif;
      font-size: 12px;
      float: left;
      margin-right: 22px;
    }

    .summary table{ width: 100%; border-collapse: collapse; }

    .summary td{
      border-bottom: 1px solid rgba(0,0,0,.12);
      padding: 6px 8px;
    }

    .summary tr:last-child td{ border-bottom: 0; }
    .summary td:first-child{ color: rgba(0,0,0,.75); width: 58%; }
    .summary td:last-child{ font-weight: 700; color: rgba(0,0,0,.85); }

    .main-text{
      overflow: hidden;
      font-size: 20px;
      line-height: 1.5;
      color: rgba(0,0,0,.85);
    }

    .main-text h2{
      margin: 10px 0 8px 0;
      font-size: 26px;
      font-weight: 600;
      color: rgba(0,0,0,.9);
    }

    .main-text p{ margin: 0 0 12px 0; }

    .report-html{
      font-size: 18px;
      line-height: 1.5;
      color: rgba(0,0,0,.85);
    }

    .report-html p{ margin: 0 0 10px 0; }
  </style>
</head>

<body class="preview">
  <section class="page cover">
    <div class="text-layer">
      <div class="fate-number">{{fate_number}}</div>

      <div class="numerolog-block">
        <div class="label">Numerolog</div>
        <p class="name">{{numerologist_name}}</p>
      </div>

      <div class="client-first">{{client_first_name}} {{client_middle_name}}</div>
      <div class="client-last">{{client_last_name}}</div>
    </div>
  </section>

  <section class="page content">
    <div class="topbar">
      <span>{{client_full_name}}-Kendi Yolculuğum</span>
    </div>

    <div class="content-area">
      <div class="name-grid">
        <table>
          <tr>
            {{name_letters_cells}}
          </tr>
          <tr>
            {{name_numbers_cells}}
          </tr>
        </table>
      </div>

      <div class="summary">
        <table>
          <tr><td>Kader Sayısı</td><td>{{kader_sayisi_text}}</td></tr>
          <tr><td>Rengi</td><td>{{rengi}}</td></tr>
          <tr><td>Elementi</td><td>{{elementi}}</td></tr>
          <tr><td>Doğum Günü Numarası</td><td>{{dogum_gunu_numarasi}}</td></tr>
          <tr><td>İfade Numarası</td><td>{{ifade_numarasi}}</td></tr>
          <tr><td>Kalp Arzusu</td><td>{{kalp_arzusu}}</td></tr>
          <tr><td>Kişilik Numarası</td><td>{{kisisilik_numarasi}}</td></tr>
          <tr><td>Karmik Borç</td><td>{{karmik_borc}}</td></tr>
          <tr><td>Yılın Öngörüsü</td><td>{{yilin_ongorusu}}</td></tr>
          <tr><td>Pin Kodları</td><td>{{pin_kodlari}}</td></tr>
        </table>
      </div>

      <div class="main-text">
        <p style="margin-top:6px;">Merhabalar {{client_first_name}},</p>
        <h2>Kader Sayın {{fate_number}}</h2>
        <div class="report-html">{{report_html}}</div>
      </div>
    </div>

    <div class="footer">
      <div class="left">{{numerologist_name}}</div>
      <div class="center">{{numerologist_phone}}</div>
      <div class="right">{{numerologist_instagram}}</div>
    </div>
  </section>
</body>
</html>`;

    let result = template;
    for (const [key, value] of Object.entries(data)) {
      result = result
        .split(`{{${key}}}`)
        .join(value)
        .split(`{${key}}`)
        .join(value);
    }

    return result;
  }, [analysis, input, queryKey, reportHtml, status]);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="font-display text-3xl font-semibold tracking-tight">
          PDF Önizleme
        </h1>
        <p className="text-sm text-muted-foreground">
          Bu sayfa; kapak + iç sayfa tasarımını burada canlı görmeniz için.
        </p>
      </div>

      {errorMessage ? (
        <div className="rounded-2xl border bg-muted/30 p-4 text-sm text-muted-foreground">
          {errorMessage}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border bg-background">
        <iframe
          title="PDF Önizleme"
          className="h-[80vh] w-full"
          srcDoc={html}
          sandbox="allow-same-origin"
        />
      </div>

      <p className="text-sm text-muted-foreground">
        Not: WordPress tarafındaki gerçek PDF çıktısı, burada gördüğünüz tasarımı
        MPDF ile üretecek.
      </p>
    </div>
  );
}
